import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';

const Gallery = () => {
  const [gallery, setGallery] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchGallery();
  }, []);

  const fetchGallery = async () => {
    try {
      setLoading(true);
      setError('');
      
      console.log('Fetching gallery from:', API_ENDPOINTS.PUBLIC.GALLERY);
      
      const response = await axios.get(API_ENDPOINTS.PUBLIC.GALLERY, {
        timeout: 8000, // 8 second timeout
      });
      
      console.log('Gallery data:', response.data);
      
      if (response.data && Array.isArray(response.data)) {
        setGallery(response.data);
      } else {
        throw new Error('Invalid data format received');
      }
      
    } catch (error) {
      console.error('Error fetching gallery:', error);
      
      // Enhanced error handling
      if (error.code === 'ECONNABORTED') {
        setError('Request timeout. Please check your connection and try again.');
      } else if (error.response) {
        setError(`Server error: ${error.response.status}. Please try again later.`);
      } else if (error.request) {
        setError('Network error. Please check your connection and ensure the server is running.');
      } else {
        setError('Unable to load gallery. Please try again later.');
      }
      
      setGallery([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = () => {
    fetchGallery();
  };

  const openImageModal = (item) => {
    setSelectedImage(item);
  };

  const closeImageModal = () => {
    setSelectedImage(null);
  };

  const handleBackdropClick = (e) => {
    if (e.target.classList.contains('modal-backdrop')) {
      closeImageModal();
    }
  };

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.keyCode === 27) {
        closeImageModal();
      }
    };

    if (selectedImage) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
      // Hide the main content when modal is open
      document.querySelector('.container.py-5').style.display = 'none';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
      // Show the main content when modal closes
      const mainContent = document.querySelector('.container.py-5');
      if (mainContent) {
        mainContent.style.display = 'block';
      }
    };
  }, [selectedImage]);

  if (loading) {
    return (
      <div className="container py-5">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading gallery...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container py-5">
        <div className="alert alert-warning text-center">
          <i className="fas fa-exclamation-triangle me-2"></i>
          {error}
          <div className="mt-3">
            <button className="btn btn-primary btn-sm" onClick={handleRetry}>
              <i className="fas fa-redo me-2"></i>
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="container py-5">
        <h1 className="text-center mb-5">Our Gallery</h1>
        
        {gallery.length === 0 ? (
          <div className="text-center py-5">
            <i className="fas fa-images fa-3x text-muted mb-3"></i>
            <p className="text-muted">No gallery images available at the moment.</p>
            <p className="text-muted small">Please check back later for updates.</p>
          </div>
        ) : (
          <div className="row">
            {gallery.map(item => (
              <div key={item.id} className="col-md-4 mb-4">
                <div 
                  className="card gallery-card h-100 shadow-sm"
                  style={{ cursor: 'pointer' }}
                  onClick={() => openImageModal(item)}
                >
                  <img 
                    src={item.image_link} 
                    className="card-img-top" 
                    alt={item.description || 'Gallery image'}
                    style={{ 
                      height: '250px', 
                      objectFit: 'cover',
                      transition: 'transform 0.3s ease'
                    }}
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/300x250/f8f9fa/6c757d?text=Image+Not+Available';
                    }}
                    onMouseOver={(e) => {
                      e.target.style.transform = 'scale(1.05)';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.transform = 'scale(1)';
                    }}
                  />
                  <div className="card-body text-center">
                    <p className="card-text text-muted">
                      {item.description || 'No description'}
                    </p>
                    <small className="text-primary">
                      <i className="fas fa-expand me-1"></i>
                      Click to view full size
                    </small>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Enhanced Full-Screen Image Modal */}
      {selectedImage && (
        <div 
          className="modal-backdrop"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: '#000000',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden'
          }}
          onClick={handleBackdropClick}
        >
          {/* Close Button */}
          <button
            type="button"
            className="btn-close btn-close-white position-absolute"
            style={{
              top: '30px',
              right: '30px',
              zIndex: 10000,
              backgroundColor: 'rgba(255,255,255,0.3)',
              padding: '12px',
              border: 'none',
              borderRadius: '4px',
              fontSize: '1.2rem'
            }}
            onClick={closeImageModal}
            aria-label="Close"
          />

          {/* Navigation Arrows */}
          <button
            className="btn btn-outline-light position-absolute"
            style={{
              left: '30px',
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 10000,
              padding: '15px 20px',
              fontSize: '1.5rem',
              backgroundColor: 'rgba(0,0,0,0.5)',
              border: '2px solid rgba(255,255,255,0.7)'
            }}
            onClick={(e) => {
              e.stopPropagation();
              const currentIndex = gallery.findIndex(item => item.id === selectedImage.id);
              const prevIndex = currentIndex > 0 ? currentIndex - 1 : gallery.length - 1;
              setSelectedImage(gallery[prevIndex]);
            }}
          >
            <i className="fas fa-chevron-left"></i>
          </button>

          <button
            className="btn btn-outline-light position-absolute"
            style={{
              right: '30px',
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 10000,
              padding: '15px 20px',
              fontSize: '1.5rem',
              backgroundColor: 'rgba(0,0,0,0.5)',
              border: '2px solid rgba(255,255,255,0.7)'
            }}
            onClick={(e) => {
              e.stopPropagation();
              const currentIndex = gallery.findIndex(item => item.id === selectedImage.id);
              const nextIndex = currentIndex < gallery.length - 1 ? currentIndex + 1 : 0;
              setSelectedImage(gallery[nextIndex]);
            }}
          >
            <i className="fas fa-chevron-right"></i>
          </button>

          {/* Image Counter */}
          <div 
            className="position-absolute text-white"
            style={{
              top: '30px',
              left: '30px',
              zIndex: 10000,
              backgroundColor: 'rgba(0,0,0,0.7)',
              padding: '8px 16px',
              borderRadius: '20px',
              fontSize: '1rem',
              fontWeight: 'bold'
            }}
          >
            {gallery.findIndex(item => item.id === selectedImage.id) + 1} / {gallery.length}
          </div>

          {/* Main Image Container */}
          <div 
            className="d-flex flex-column align-items-center justify-content-center"
            style={{
              width: '95%',
              height: '95%',
              padding: '20px'
            }}
          >
            <img
              src={selectedImage.image_link}
              alt={selectedImage.description || 'Gallery image'}
              style={{
                maxWidth: '100%',
                maxHeight: '85vh',
                objectFit: 'contain',
                borderRadius: '8px',
                boxShadow: '0 10px 30px rgba(0,0,0,0.5)'
              }}
              onError={(e) => {
                e.target.src = 'https://via.placeholder.com/800x600/333/fff?text=Image+Not+Available';
              }}
            />

            {/* Description */}
            {selectedImage.description && (
              <div 
                className="text-white text-center mt-4 p-3"
                style={{
                  backgroundColor: 'rgba(255,255,255,0.15)',
                  borderRadius: '10px',
                  maxWidth: '80%',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255,255,255,0.2)'
                }}
              >
                <p className="mb-0 fs-5">{selectedImage.description}</p>
              </div>
            )}
          </div>
        </div>
      )}

      <style jsx>{`
        .gallery-card {
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        .gallery-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 8px 25px rgba(0,0,0,0.15) !important;
        }
      `}</style>
    </>
  );
};

export default Gallery;